<template>
  <div class="client-teus-dashboard">
    <!-- Header Section -->
    <div class="dashboard-header">
      <div>
        <h2 class="dashboard-title">
          {{
            isFromSalesPersonDashboard
              ? "Client TEUs Dashboard"
              : "Sales Person-Wise TEUs Dashboard"
          }}
        </h2>
        <p
          v-if="isFromSalesPersonDashboard && selectedClientName"
          class="client-info"
        >
          <i class="fa fa-building"></i>
          Client: <strong>{{ selectedClientName }}</strong>
        </p>
      </div>
      <div class="header-actions">
        <button
          class="btn btn-secondary btn-sm"
          @click="goBack"
          v-if="isFromSalesPersonDashboard"
        >
          <i class="fa fa-arrow-left"></i>
          Back to Sales Person Dashboard
        </button>
        <button
          class="btn btn-primary btn-sm"
          @click="refreshData"
          :disabled="isLoading"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': isLoading }"></i>
          Refresh
        </button>
        <button
          class="btn btn-success btn-sm"
          @click="exportToExcel"
          :disabled="isLoading || !salesPersonData.length"
        >
          <i class="fa fa-file-excel-o"></i>
          Export to Excel
        </button>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="isLoading" class="loading-container">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2">Loading TEUs data...</p>
    </div>

    <!-- Error Message -->
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <i class="fa fa-exclamation-triangle"></i>
      {{ errorMessage }}
      <button type="button" class="close" @click="errorMessage = ''">
        <span>&times;</span>
      </button>
    </div>
    <b-card class="formCard">
      <b-row>
        <b-col md="3">
           <label class="mb-1" style="font-size: 12px; font-weight: 600">Date *</label>
          <date-range-picker
            class="mb-1"
            :always-show-calendars="showCal"
            v-model="Filter.dateRange"
            :startDate="startDate"
            :endDate="endDate"
            :locale-data="locale"
            @update="fetchData"
          />
        </b-col>
        <b-col md="2">
           <label class="mb-1" style="font-size: 12px; font-weight: 600">Branch *</label>
          <model-list-select
            class="mb-1"
            :list="$store.getters['common/getAllBranchList']"
            id="branchfilter"
            v-model="Filter.branchfilter"
            option-value="pickupId"
            option-text="pickupLocation"
            placeholder="All"
            @input="fetchData"
            :isDisabled="
              $sessionsStorage.get('designation') != 'Admin' ? true : false
            "
          />
        </b-col>
        <b-col md="2">
           <label class="mb-1" style="font-size: 12px; font-weight: 600">Transport Type *</label>
          <model-list-select
            class="mb-1"
            :list="transportTypeList"
            id="transportTypeFilter"
            v-model="Filter.transportTypeFilter"
            option-value="transportTypeVal"
            option-text="transportTypeText"
            placeholder="All"
            @input="fetchData"
          />
        </b-col>
        <b-col md="2">
           <label class="mb-1" style="font-size: 12px; font-weight: 600">Nomination Shipper *</label>
          <model-list-select
            class="mb-1"
            :list="nominationShipperList"
            id="is_nomination_shipper"
            v-model="Filter.is_nomination_shipper"
            option-value="value"
            option-text="text"
            placeholder="All Shippers"
            @input="fetchData"
          />
        </b-col>
        <b-col md="2" v-if="!isFromSalesPersonDashboard">
          <label class="mb-1" style="font-size: 12px; font-weight: 600">Sales Person *</label>
          <model-list-select
            class="mb-1"
            :isDisabled="($sessionsStorage.get('designation') != 'Admin' || $sessionsStorage.get('nameUser') != 'Bhavneesh') ? true : false"
            :list="$store.getters['common/getAllUserList']"
            id="salesPersonFilter"
            v-model="Filter.salesPersonFilter"
            option-value="userId"
            option-text="employeeName"
            placeholder="Select Sales Person"
            @input="fetchData"
          />
        </b-col>
        <b-col md="2" v-else>
          <label class="mb-1" style="font-size: 12px; font-weight: 600"
            >Selected Sales Person</label
          >
          <div class="selected-info">
            <span class="badge badge-info">{{
              getSalesPersonName(Filter.salesPersonFilter)
            }}</span>
          </div>
        </b-col>
        <b-col md="1">
          <button
            class="btn btn-warning btn-sm mt-2"
            @click="clearFilters"
            :disabled="isLoading"
          >
            <i class="fa fa-times"></i>
            Clear
          </button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Dashboard Content -->
    <div
      v-if="!isLoading && salesPersonData.length > 0"
      class="dashboard-content"
    >
      <!-- Summary Cards -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-primary">
              <i class="fa fa-building"></i>
            </div>
            <div class="card-content">
              <h4>
                {{ isFromSalesPersonDashboard ? "1" : totalSalesPersons }}
              </h4>
              <p>
                {{ isFromSalesPersonDashboard ? "Client" : "Total Clients" }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-success">
              <i class="fa fa-cubes"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalTeus }}</h4>
              <p>Total TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-info">
              <i class="fa fa-ship"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalExportTeus }}</h4>
              <p>Export TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-2">
          <div class="summary-card">
            <div class="card-icon bg-warning">
              <i class="fa fa-truck"></i>
            </div>
            <div class="card-content">
              <h4>{{ totalImportTeus }}</h4>
              <p>Import TEUs</p>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-info">
              <i class="fa fa-line-chart"></i>
            </div>
            <div class="card-content">
              <h4>{{ averageTeusPerSalesPerson }}</h4>
              <p>Avg TEUs/Client</p>
            </div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="summary-card">
            <div class="card-icon bg-warning">
              <i class="fa fa-star"></i>
            </div>
            <div class="card-content">
              <h4>{{ topSalesPerson.name }}</h4>
              <p>Top Client ({{ topSalesPerson.teus }} TEUs)</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section - Collapsible -->
      <div class="row mb-4">
        <div class="col-md-12">
          <div class="card">
            <div
              class="card-header"
              style="cursor: pointer"
              @click="showCharts = !showCharts"
            >
              <h5 class="mb-0">
                <i
                  class="fa"
                  :class="showCharts ? 'fa-chevron-down' : 'fa-chevron-right'"
                ></i>
                Charts & Visualizations
                <span class="badge badge-info ml-2">{{
                  showCharts ? "Hide" : "Show"
                }}</span>
              </h5>
            </div>
            <div class="card-body" v-show="showCharts">
              <!-- Flexible Charts (Two Rows) -->
              <div class="row mb-4">
                <div class="col-md-12">
                  <client-teus-flexible-chart
                    :chart-data="allClientsChartData"
                    :chart-height="500"
                    chart-type="bar"
                    chart-title="Clients TEUs Overview"
                    chart-icon="fa-bar-chart"
                    :default-limit="20"
                  />
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <client-teus-flexible-chart
                    :chart-data="allClientsChartData"
                    :chart-height="450"
                    chart-type="bar"
                    chart-title="Top Performers"
                    chart-icon="fa-trophy"
                    :default-limit="10"
                  />
                </div>
                <div class="col-md-12">
                  <client-teus-flexible-chart
                    :chart-data="allClientsChartData"
                    :chart-height="450"
                    chart-type="pie"
                    chart-title="TEUs Distribution"
                    chart-icon="fa-pie-chart"
                    :default-limit="10"
                  />
                </div>
                <div class="col-md-12">
                  <div class="chart-card">
                    <div class="chart-header">
                      <div class="chart-title">
                        <i class="fa fa-line-chart"></i>
                        Monthly TEUs Trend
                        <span class="badge badge-primary ml-2"
                          >{{ selectedTrendSalesPersons.length }} Clients</span
                        >
                        <span class="badge badge-info ml-2"
                          >{{ dynamicTrendChartHeight }}px</span
                        >
                      </div>
                      <div class="chart-controls">
                        <label class="control-label">Show:</label>
                        <select
                          v-model="trendSalesPersonLimit"
                          @change="updateTrendSalesPersons"
                          class="form-control form-control-sm"
                        >
                          <option value="5">Top 5</option>
                          <option value="10">Top 10</option>
                          <option value="15">Top 15</option>
                          <option value="20">Top 20</option>
                          <option value="all">All</option>
                        </select>
                      </div>
                    </div>
                    <client-teus-line-chart
                      :chart-data="monthlyTrendChartData"
                      :chart-height="dynamicTrendChartHeight"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-card">
        <h4 class="table-title">
          <i class="fa fa-table"></i>
          {{
            isFromSalesPersonDashboard
              ? "Client TEUs Details"
              : "Detailed TEUs Data"
          }}
          <span class="badge badge-primary ml-2">{{
            isFromSalesPersonDashboard
              ? "1 Client"
              : filteredAndSortedData.length + " Clients"
          }}</span>
        </h4>
        <div class="table-filters mb-3">
          <div class="row align-items-center">
            <div class="col-md-6">
              <input
                type="text"
                class="form-control"
                v-model="searchQuery"
                placeholder="Search by client name..."
                style="max-width: 300px"
              />
            </div>
            <div class="col-md-6">
              <div class="color-legend">
                <small class="text-muted">
                  <strong>Color Legend:</strong>
                  <span class="text-success font-weight-bold ml-2"
                    >🟢 High (>30 TEUs)</span
                  >
                  <span class="text-info ml-2">🔵 Good (20-30 TEUs)</span>
                  <span class="text-warning ml-2">🟡 Medium (10-20 TEUs)</span>
                  <span class="text-muted ml-2">⚪ Low (<10 TEUs)</span>
                </small>
              </div>
            </div>
          </div>
        </div>
        <div class="table-responsive">
          <table class="table table-striped table-bordered table-hover">
            <thead>
              <tr>
                <th rowspan="2" class="excel-header">S.No</th>

                <th
                  rowspan="2"
                  class="excel-header"
                  @click="sortBy('clientName')"
                  style="cursor: pointer"
                >
                  Client Name
                  <i class="fa fa-sort" v-if="sortColumn !== 'clientName'"></i>
                  <i
                    class="fa fa-sort-asc"
                    v-if="
                      sortColumn === 'clientName' && sortDirection === 'asc'
                    "
                  ></i>
                  <i
                    class="fa fa-sort-desc"
                    v-if="
                      sortColumn === 'clientName' && sortDirection === 'desc'
                    "
                  ></i>
                </th>
                <th rowspan="2" class="excel-header">
                  Nomination Shipper/Type
                </th>
                <th rowspan="2" class="excel-header">Branch</th>
                <th
                  v-for="month in uniqueMonths"
                  :key="month"
                  :colspan="
                    Filter.transportTypeFilter === 'All'
                      ? 3
                      : Filter.transportTypeFilter === 'Export'
                        ? 1
                        : 1
                  "
                  class="excel-month-header"
                >
                  {{ month }}
                </th>
                <th
                  rowspan="2"
                  class="excel-total-header"
                  @click="sortBy('totalExport')"
                  style="cursor: pointer"
                  v-if="
                    Filter.transportTypeFilter === 'Export' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  Export Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'totalExport'"></i>
                  <i
                    class="fa fa-sort-asc"
                    v-if="
                      sortColumn === 'totalExport' && sortDirection === 'asc'
                    "
                  ></i>
                  <i
                    class="fa fa-sort-desc"
                    v-if="
                      sortColumn === 'totalExport' && sortDirection === 'desc'
                    "
                  ></i>
                </th>
                <th
                  rowspan="2"
                  class="excel-total-header"
                  @click="sortBy('totalImport')"
                  style="cursor: pointer"
                  v-if="
                    Filter.transportTypeFilter === 'Import' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  Import Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'totalImport'"></i>
                  <i
                    class="fa fa-sort-asc"
                    v-if="
                      sortColumn === 'totalImport' && sortDirection === 'asc'
                    "
                  ></i>
                  <i
                    class="fa fa-sort-desc"
                    v-if="
                      sortColumn === 'totalImport' && sortDirection === 'desc'
                    "
                  ></i>
                </th>
                <th
                  rowspan="4"
                  class="excel-total-header"
                  @click="sortBy('total')"
                  style="cursor: pointer"
                  v-if="Filter.transportTypeFilter === 'All'"
                >
                  Total
                  <i class="fa fa-sort" v-if="sortColumn !== 'total'"></i>
                  <i
                    class="fa fa-sort-asc"
                    v-if="sortColumn === 'total' && sortDirection === 'asc'"
                  ></i>
                  <i
                    class="fa fa-sort-desc"
                    v-if="sortColumn === 'total' && sortDirection === 'desc'"
                  ></i>
                </th>
              </tr>
              <tr>
                <template v-for="month in uniqueMonths">
                  <th
                    class="excel-sub-header excel-total"
                    v-if="Filter.transportTypeFilter === 'All'"
                  >
                    Total
                  </th>
                  <th
                    class="excel-sub-header excel-export"
                    v-if="
                      Filter.transportTypeFilter === 'Export' ||
                      Filter.transportTypeFilter === 'All'
                    "
                  >
                    Export
                  </th>
                  <th
                    class="excel-sub-header excel-import"
                    v-if="
                      Filter.transportTypeFilter === 'Import' ||
                      Filter.transportTypeFilter === 'All'
                    "
                  >
                    Import
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(client, index) in filteredAndSortedData" :key="index">
                <td>{{ index + 1 }}</td>
                <td
                  @click="navigateToSalesPersonDashboard(client)"
                  style="cursor: pointer"
                  class="client-name-cell"
                >
                  <div>
                    <strong>{{
                      client.clientName || client.salesPersonName
                    }}</strong>
                    <i
                      class="fa fa-external-link ml-1 text-primary"
                      style="font-size: 12px"
                    ></i>
                  </div>
                  <div
                    v-if="client.loginClientTime || client.updateClientTime"
                    class="client-times text-muted small mt-1"
                  >
                    <div v-if="client.loginClientTime">
                      <i class="fa fa-calendar-plus-o"></i> Created:
                      {{ client.loginClientTime }}
                    </div>
                    <div v-if="client.CreatedBy">
                      <i class="fa fa-user"></i> Created By:
                      {{ client.CreatedBy }}
                    </div>
                  </div>
                </td>
                <td>
                  {{ client.NominationShipper || client.NominationShipper }}/
                  {{ client.Type || client.Type }}
                </td>
                <td>
                  {{ client.BranchName || client.BranchName }}
                </td>
                <template v-for="month in uniqueMonths">
                  <td
                    v-if="Filter.transportTypeFilter === 'All'"
                    class="excel-cell excel-total-cell"
                    :class="getTeusClass(client.monthsMap[month])"
                    :title="getTeusTooltip(client.monthsMap[month])"
                  >
                    {{ client.monthsMap[month] || "-" }}
                  </td>
                  <td
                    v-if="
                      Filter.transportTypeFilter === 'Export' ||
                      Filter.transportTypeFilter === 'All'
                    "
                    class="excel-cell excel-export-cell"
                    :class="getTeusClass(client.exportMonthsMap[month])"
                    :title="getTeusTooltip(client.exportMonthsMap[month])"
                  >
                    {{ client.exportMonthsMap[month] || "-" }}
                  </td>
                  <td
                    v-if="
                      Filter.transportTypeFilter === 'Import' ||
                      Filter.transportTypeFilter === 'All'
                    "
                    class="excel-cell excel-import-cell"
                    :class="getTeusClass(client.importMonthsMap[month])"
                    :title="getTeusTooltip(client.importMonthsMap[month])"
                  >
                    {{ client.importMonthsMap[month] || "-" }}
                  </td>
                </template>
                <td
                  class="excel-cell excel-total-cell"
                  v-if="
                    Filter.transportTypeFilter === 'Export' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  <strong>{{ client.totalExport }}</strong>
                </td>
                <td
                  class="excel-cell excel-import-cell"
                  v-if="
                    Filter.transportTypeFilter === 'Import' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  <strong>{{ client.totalImport }}</strong>
                </td>
                <td
                  class="excel-cell excel-total-cell"
                  v-if="Filter.transportTypeFilter === 'All'"
                >
                  <strong>{{ client.total }}</strong>
                </td>
              </tr>
            </tbody>
            <tfoot v-if="filteredAndSortedData.length > 0">
              <tr class="excel-footer-row">
                <td colspan="4" class="excel-footer-label">
                  <strong>Total</strong>
                </td>
                <template v-for="month in uniqueMonths">
                  <td
                    class="excel-cell excel-total-cell"
                    v-if="Filter.transportTypeFilter === 'All'"
                  >
                    <strong>{{ getMonthTotal(month) }}</strong>
                  </td>
                  <td
                    class="excel-cell excel-export-cell"
                    v-if="
                      Filter.transportTypeFilter === 'Export' ||
                      Filter.transportTypeFilter === 'All'
                    "
                  >
                    <strong>{{ getMonthExportTotal(month) }}</strong>
                  </td>
                  <td
                    class="excel-cell excel-import-cell"
                    v-if="
                      Filter.transportTypeFilter === 'Import' ||
                      Filter.transportTypeFilter === 'All'
                    "
                  >
                    <strong>{{ getMonthImportTotal(month) }}</strong>
                  </td>
                </template>
                <td
                  class="excel-cell excel-export-cell"
                  v-if="
                    Filter.transportTypeFilter === 'Export' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  <strong>{{ totalExportTeus }}</strong>
                </td>
                <td
                  class="excel-cell excel-import-cell"
                  v-if="
                    Filter.transportTypeFilter === 'Import' ||
                    Filter.transportTypeFilter === 'All'
                  "
                >
                  <strong>{{ totalImportTeus }}</strong>
                </td>
                <td
                  class="excel-cell excel-total-cell"
                  v-if="Filter.transportTypeFilter === 'All'"
                >
                  <strong>{{ totalTeus }}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div v-if="filteredAndSortedData.length === 0" class="text-center py-4">
          <p class="text-muted">No data found matching your search criteria.</p>
        </div>
      </div>
    </div>

    <!-- No Data Message -->
    <div
      v-if="!isLoading && salesPersonData.length === 0 && !errorMessage"
      class="no-data-container"
    >
      <i class="fa fa-inbox fa-3x text-muted mb-3"></i>
      <h4>No Data Available</h4>
      <p class="text-muted">There is no TEUs data available at the moment.</p>
      <button class="btn btn-primary" @click="refreshData">
        <i class="fa fa-refresh"></i>
        Try Again
      </button>
    </div>
  </div>
</template>

<script>
import SalesPersonTeusService from "../../services/SalesPersonTeusService";
import ClientTeusBarChart from "./ClientTeusBarChart.vue";
import ClientTeusLineChart from "./ClientTeusLineChart.vue";
import ClientTeusAllClientsChart from "./ClientTeusAllClientsChart.vue";
import ClientTeusFlexibleChart from "./ClientTeusFlexibleChart.vue";
import * as XLSX from "xlsx";
import { ModelListSelect } from "vue-search-select";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
  name: "SalesPersonTeusDashboard",
  beforeMount() {
    this.$store.dispatch(
      "common/setAllBranchList",
      JSON.stringify({
        filter: "BranchList",
        active: "all",
      }),
    );
    this.$store.dispatch(
      "common/setAllUserList",
      JSON.stringify({
        itemCode: "GET_USERLIST_Data",
      }),
    );
    this.$store.dispatch(
      "setCurrentRoute",
      "/dashboard/sales-person-teus-dashboard",
    );

    // Check for query parameters (client navigation from sales person dashboard)
    if (this.$route.query.clientId) {
      this.selectedClientId = this.$route.query.clientId;
      this.selectedClientName = this.$route.query.clientName;
      this.Filter.salesPersonFilter = this.$route.query.salesPersonId;
      this.isFromSalesPersonDashboard = true;
    }

    if (
      this.$sessionsStorage.get("loginId") != undefined &&
      this.$sessionsStorage.get("loginId") != "" &&
      this.$sessionsStorage.get("departmentName") != undefined &&
      this.$sessionsStorage.get("departmentName") != "" &&
      this.$sessionsStorage.get("designation") != undefined &&
      this.$sessionsStorage.get("designation") != ""
    ) {
      this.fetchData();
    } else {
      this.$sessionsStorage.set("loginId", "");
      this.$router.push({
        path: "/Login/",
      });
    }
  },
  components: {
    ClientTeusBarChart,
    ClientTeusLineChart,
    ClientTeusAllClientsChart,
    ClientTeusFlexibleChart,
    ModelListSelect,
    DateRangePicker,
  },

  data() {
    return {
      openLeft: "left",
      showCal: false,
      startDate: "2017-09-05",
      endDate: "2017-09-15",
      Filter: {
        transportTypeFilter: "All",
        branchfilter: "",
        salesPersonFilter: (this.$sessionsStorage.get('designation') != 'Admin' || this.$sessionsStorage.get('nameUser') != 'Bhavneesh') ? this.$sessionsStorage.get('userId') : "",
        is_nomination_shipper: "",
        dateRange: {
          startDate: "",
          endDate: "",
        },
      },
      locale: {
        direction: "ltr",
        format: "mm/dd/yyyy",
        separator: " - ",
        applyLabel: "Apply",
        cancelLabel: "Cancel",
        weekLabel: "W",
        customRangeLabel: "Custom Range",
        daysOfWeek: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
        monthNames: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        firstDay: 0,
      },
      value1: [new Date(2019, 9, 8), new Date(2019, 9, 19)],
      value2: [],
      langType: {
        pickers: ["Today", "Yesterday", "This Month", "This Year"],
      },
      time: "",
      date: "",
      week: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],

      langEnq: {
        pickers: ["", "", "previous 7 days", "previous 30 days"],
      },
      transportTypeList: [
        {
          transportTypeVal: "Export",
          transportTypeText: "Export",
        },
        {
          transportTypeVal: "Import",
          transportTypeText: "Import",
        },
        {
          transportTypeVal: "All",
          transportTypeText: "All",
        },
      ],
      nominationShipperList: [
        {
          value: "1",
          text: "Nominated Shippers",
        },
        {
          value: "0",
          text: "Free Hand Shippers",
        },
        {
          value: "",
          text: "All Shippers",
        },
      ],

      salesPersonData: [],
      isLoading: false,
      errorMessage: "",
      searchQuery: "",
      sortColumn: "total",
      sortDirection: "desc",
      showCharts: false,
      trendSalesPersonLimit: "5",
      selectedTrendSalesPersons: [],
      selectedClientId: null,
      selectedClientName: null,
      isFromSalesPersonDashboard: false,
    };
  },

  computed: {
    // Get unique months from all clients
    uniqueMonths() {
      const monthsSet = new Set();
      this.salesPersonData.forEach((client) => {
        if (client.months && Array.isArray(client.months)) {
          client.months.forEach((m) => {
            monthsSet.add(m.month + " " + m.year);
          });
        }
      });
      return Array.from(monthsSet).sort((a, b) => {
        const [monthA, yearA] = a.split(" ");
        const [monthB, yearB] = b.split(" ");
        const monthOrder = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        return monthOrder.indexOf(monthA) - monthOrder.indexOf(monthB);
      });
    },

    // Process data for table display
    processedTableData() {
      return this.salesPersonData.map((client) => {
        const monthsMap = {};
        const exportMonthsMap = {};
        const importMonthsMap = {};
        let total = 0;
        let totalExport = 0;
        let totalImport = 0;

        if (client.months && Array.isArray(client.months)) {
          client.months.forEach((m) => {
            const key = m.month + " " + m.year;
            monthsMap[key] = m.totalTeus;
            exportMonthsMap[key] = m.exportTeus || 0;
            importMonthsMap[key] = m.importTeus || 0;
            total += m.totalTeus;
            totalExport += m.exportTeus || 0;
            totalImport += m.importTeus || 0;
          });
        }

        return {
          clientName: client.clientName || client.salesPersonName || "Unknown",
          salesPersonName: client.clientName || client.salesPersonName,
          loginClientTime: client.loginClientTime,
          updateClientTime: client.updateClientTime,
          CreatedBy: client.CreatedBy,
          BranchName: client.BranchName,
          NominationShipper: client.NominationShipper,
          Type: client.Type,
          monthsMap,
          exportMonthsMap,
          importMonthsMap,
          total,
          totalExport,
          totalImport,
        };
      });
    },

    // Filter and sort data
    filteredAndSortedData() {
      let data = this.processedTableData;

      // Apply search filter
      if (this.searchQuery) {
        data = data.filter((item) => {
          const clientName = (
            item.clientName ||
            item.salesPersonName ||
            ""
          ).toLowerCase();
          return clientName.includes(this.searchQuery.toLowerCase());
        });
      }

      // Apply sorting
      if (this.sortColumn) {
        data.sort((a, b) => {
          let aVal, bVal;

          if (this.sortColumn === "clientName") {
            aVal = (a.clientName || a.salesPersonName).toLowerCase();
            bVal = (b.clientName || b.salesPersonName).toLowerCase();
          } else if (this.sortColumn === "totalExport") {
            aVal = a.totalExport;
            bVal = b.totalExport;
          } else if (this.sortColumn === "totalImport") {
            aVal = a.totalImport;
            bVal = b.totalImport;
          } else {
            aVal = a.total;
            bVal = b.total;
          }

          if (this.sortDirection === "asc") {
            return aVal > bVal ? 1 : -1;
          } else {
            return aVal < bVal ? 1 : -1;
          }
        });
      }

      return data;
    },

    // Summary statistics
    totalSalesPersons() {
      return this.salesPersonData.filter(
        (sp) => sp.months && sp.months.length > 0,
      ).length;
    },

    totalTeus() {
      return this.salesPersonData.reduce((sum, client) => {
        return (
          sum +
          (client.months
            ? client.months.reduce((s, m) => s + (m.totalTeus || 0), 0)
            : 0)
        );
      }, 0);
    },

    totalExportTeus() {
      return this.salesPersonData.reduce((sum, client) => {
        return (
          sum +
          (client.months
            ? client.months.reduce((s, m) => s + (m.exportTeus || 0), 0)
            : 0)
        );
      }, 0);
    },

    totalImportTeus() {
      return this.salesPersonData.reduce((sum, client) => {
        return (
          sum +
          (client.months
            ? client.months.reduce((s, m) => s + (m.importTeus || 0), 0)
            : 0)
        );
      }, 0);
    },

    averageTeusPerSalesPerson() {
      return this.totalSalesPersons > 0
        ? Math.round(this.totalTeus / this.totalSalesPersons)
        : 0;
    },

    topSalesPerson() {
      let top = { name: "N/A", teus: 0 };
      this.processedTableData.forEach((client) => {
        if (client.total > top.teus) {
          top = {
            name: client.clientName || client.salesPersonName,
            teus: client.total,
          };
        }
      });
      return top;
    },

    // Chart data for all clients (horizontal bar chart)
    allClientsChartData() {
      const sortedData = Object.assign([], this.processedTableData).sort(
        (a, b) => b.total - a.total,
      ); // Sort by total descending

      return {
        labels: sortedData.map((c) => c.clientName || c.salesPersonName),
        datasets: [
          {
            label: "Total TEUs",
            data: sortedData.map((c) => c.total),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      };
    },

    // Chart data for bar chart (top 10)
    clientTeusChartData() {
      const sortedData = Object.assign([], this.processedTableData)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10); // Top 10 clients

      return {
        labels: sortedData.map((c) => c.clientName || c.salesPersonName),
        datasets: [
          {
            label: "Total TEUs",
            data: sortedData.map((c) => c.total),
            backgroundColor: "rgba(54, 162, 235, 0.6)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      };
    },

    // Chart data for line chart
    monthlyTrendChartData() {
      const months = this.uniqueMonths;
      const datasets = [];

      // Use selected trend clients or default to top 5
      const clientsToShow =
        this.selectedTrendSalesPersons.length > 0
          ? this.selectedTrendSalesPersons
          : Object.assign([], this.processedTableData)
              .sort((a, b) => b.total - a.total)
              .slice(0, 5);

      const colors = [
        "rgba(255, 99, 132, 1)",
        "rgba(54, 162, 235, 1)",
        "rgba(255, 206, 86, 1)",
        "rgba(75, 192, 192, 1)",
        "rgba(153, 102, 255, 1)",
        "rgba(255, 159, 64, 1)",
        "rgba(199, 199, 199, 1)",
        "rgba(83, 102, 255, 1)",
        "rgba(255, 99, 255, 1)",
        "rgba(99, 255, 132, 1)",
        "rgba(255, 132, 99, 1)",
        "rgba(132, 99, 255, 1)",
        "rgba(255, 255, 99, 1)",
        "rgba(99, 255, 255, 1)",
        "rgba(255, 99, 99, 1)",
        "rgba(99, 99, 255, 1)",
        "rgba(255, 255, 132, 1)",
        "rgba(132, 255, 99, 1)",
        "rgba(255, 132, 255, 1)",
        "rgba(132, 255, 255, 1)",
      ];

      clientsToShow.forEach((client, index) => {
        datasets.push({
          label: client.clientName || client.salesPersonName,
          data: months.map((month) => client.monthsMap[month] || 0),
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length].replace("1)", "0.1)"),
          tension: 0.4,
          fill: false,
        });
      });

      return {
        labels: months,
        datasets,
      };
    },

    // Dynamic height for trend chart based on number of clients
    dynamicTrendChartHeight() {
      const baseHeight = 450;
      const clientCount = this.selectedTrendSalesPersons.length || 5;

      // For line charts, increase height based on number of clients
      const heightPerClient = 30; // 30px per client
      const minHeight = 350;
      const maxHeight = 800;
      const calculatedHeight = baseHeight + clientCount * heightPerClient;

      return Math.max(minHeight, Math.min(maxHeight, calculatedHeight));
    },
  },

  mounted() {
    this.fetchData();
  },

  watch: {
    processedTableData() {
      // Update trend clients when data changes
      this.updateTrendSalesPersons();
    },
  },

  methods: {
    async fetchData() {
      // Validate that required filters are selected
      if (this.isFromSalesPersonDashboard) {
        if (!this.selectedClientId || !this.Filter.salesPersonFilter) {
          this.errorMessage = "Client or sales person information is missing";
          this.salesPersonData = [];
          return;
        }
      } else {
        // if (!this.Filter.salesPersonFilter) {
        //   this.errorMessage = 'Please select a sales person to view TEUs data'
        //   this.salesPersonData = []
        //   return
        // }
      }

      this.isLoading = true;
      this.errorMessage = "";

      try {
        let response;

        if (this.isFromSalesPersonDashboard) {
          // alert("Sales Person Filter is missing", isFromSalesPersonDashboard);
          // Fetch TEUs data for specific client
          const payload = {
            is_nomination_shipper: this.Filter.is_nomination_shipper,
            fk_client_id: this.selectedClientId,
            transportTypeFilter:
              this.Filter.transportTypeFilter !== "All"
                ? this.Filter.transportTypeFilter
                : undefined,
            branchfilter: this.Filter.branchfilter || undefined,
            dateRange: this.Filter.dateRange,
            // ?.startDate + " to " + this.Filter.dateRange.endDate || undefined,
            // endDate: this.Filter.dateRange.endDate || undefined
          };

          // Call client-specific TEUs API (we'll need to create this)
          response = await SalesPersonTeusService.getClientWiseTeus(payload);
        } else {
          // alert("Sales Person Filter is missing else");
          // Fetch TEUs data for all clients of a sales person
          const payload = {
            is_nomination_shipper: this.Filter.is_nomination_shipper,
            fk_user_id: this.Filter.salesPersonFilter,
            transportTypeFilter:
              this.Filter.transportTypeFilter !== "All"
                ? this.Filter.transportTypeFilter
                : undefined,
            branchfilter: this.Filter.branchfilter || undefined,
            dateRange: this.Filter.dateRange,
            // startDate: this.Filter.dateRange.startDate || undefined,
            // endDate: this.Filter.dateRange.endDate || undefined
          };

          response =
            await SalesPersonTeusService.getSalesPersonWiseTeus(payload);
        }

        if (response.data && response.data.status) {
          const clientData = response.data.data || [];

          if (clientData.length > 0) {
            if (this.isFromSalesPersonDashboard) {
              // For client-specific view, show single client data
              this.salesPersonData = [
                {
                  salesPersonName: this.selectedClientName,
                  clientName: this.selectedClientName,
                  months: clientData.months || [],
                  loginClientTime: clientData.loginClientTime,
                  updateClientTime: clientData.updateClientTime,
                  Type: clientData.Type,
                  BranchName: clientData.BranchName,
                  NominationShipper: clientData.NominationShipper,
                  CreatedBy: clientData.CreatedBy,
                  userId: this.Filter.salesPersonFilter,
                },
              ];
            } else {
              // Transform client data to salesPersonData format for consistency with existing code
              this.salesPersonData = clientData.map((client) => ({
                salesPersonName: client.clientName,
                clientName: client.clientName,
                loginClientTime: client.loginClientTime,
                updateClientTime: client.updateClientTime,
                Type: client.Type,
                BranchName: client.BranchName,
                NominationShipper: client.NominationShipper,
                CreatedBy: client.CreatedBy,
                months: client.months || [],
                userId: this.Filter.salesPersonFilter,
              }));
            }
          } else {
            this.salesPersonData = [];
          }
        } else {
          this.errorMessage =
            response.data.message || "Failed to fetch TEUs data";
          this.salesPersonData = [];
        }
      } catch (error) {
        console.error("Error fetching TEUs data:", error);
        this.errorMessage =
          (error.response &&
            error.response.data &&
            error.response.data.message) ||
          "Failed to fetch TEUs data. Please try again later.";
        this.salesPersonData = [];
      } finally {
        this.isLoading = false;
      }
    },

    refreshData() {
      this.fetchData();
    },

    clearFilters() {
      if (this.isFromSalesPersonDashboard) {
        // When coming from sales person dashboard, only clear transport and branch filters
        this.Filter.transportTypeFilter = "All";
        this.Filter.branchfilter = "";
        this.Filter.is_nomination_shipper = "";
        this.Filter.dateRange = {
          startDate: "",
          endDate: "",
        };
      } else {
        // Normal clear for sales person dashboard
        this.Filter = {
          transportTypeFilter: "All",
          branchfilter: "",
          salesPersonFilter: (this.$sessionsStorage.get('designation') != 'Admin' || this.$sessionsStorage.get('nameUser') != 'Bhavneesh') ? this.$sessionsStorage.get('userId') : "",
          is_nomination_shipper: "",
          dateRange: {
            startDate: "",
            endDate: "",
          },
        };
        this.salesPersonData = [];
      }
      this.errorMessage = "";
      // Fetch data only if we have required filters
      if (this.isFromSalesPersonDashboard || this.Filter.salesPersonFilter) {
        this.fetchData();
      }
    },

    updateTrendSalesPersons() {
      const limit =
        this.trendSalesPersonLimit === "all"
          ? this.processedTableData.length
          : parseInt(this.trendSalesPersonLimit);

      this.selectedTrendSalesPersons = Object.assign(
        [],
        this.processedTableData,
      )
        .sort((a, b) => b.total - a.total)
        .slice(0, limit);
    },

    sortBy(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortDirection = "desc";
      }
    },

    getTeusClass(value) {
      if (!value) return "";
      if (value > 30) return "text-success font-weight-bold";
      if (value > 20) return "text-info";
      if (value > 10) return "text-warning";
      return "text-muted";
    },

    getTeusTooltip(value) {
      if (!value) return "No data available";
      if (value > 30)
        return "High Volume: " + value + " TEUs (Excellent Performance)";
      if (value > 20)
        return "Good Volume: " + value + " TEUs (Good Performance)";
      if (value > 10)
        return "Medium Volume: " + value + " TEUs (Average Performance)";
      return "Low Volume: " + value + " TEUs (Below Average Performance)";
    },

    getMonthTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.monthsMap[month] || 0);
      }, 0);
    },

    getMonthExportTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.exportMonthsMap[month] || 0);
      }, 0);
    },

    getMonthImportTotal(month) {
      return this.filteredAndSortedData.reduce((sum, client) => {
        return sum + (client.importMonthsMap[month] || 0);
      }, 0);
    },

    exportToExcel() {
      try {
        // Prepare data for Excel
        const excelData = [];

        // Add header rows
        const headerRow1 = [
          "S.No",
          "Client Name",
          "Created At",
          "Type",
          "Branch Name",
          "Nomination Shipper",
          "Created By",
          ...this.uniqueMonths.map((month) => [month, "", ""]).flat(),
          "Export Total",
          "Import Total",
          "Total",
        ];
        excelData.push(headerRow1);

        const headerRow2 = [
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          ...this.uniqueMonths
            .map((month) => ["Total", "Export", "Import"])
            .flat(),
          "",
          "",
          "",
        ];
        excelData.push(headerRow2);

        // Add data rows
        this.filteredAndSortedData.forEach((client, index) => {
          const row = [
            index + 1,
            client.clientName || client.salesPersonName,
            client.loginClientTime,
            client.Type,
            client.BranchName,
            client.NominationShipper,
            client.CreatedBy,
            ...this.uniqueMonths
              .map((month) => [
                client.monthsMap[month] || 0,
                client.exportMonthsMap[month] || 0,
                client.importMonthsMap[month] || 0,
              ])
              .flat(),
            client.totalExport,
            client.totalImport,
            client.total,
          ];
          excelData.push(row);
        });

        // Add total row
        const totalRow = [
          "",
          "Total",
          "",
          "",
          "",
          "",
          "",
          ...this.uniqueMonths
            .map((month) => [
              this.getMonthTotal(month),
              this.getMonthExportTotal(month),
              this.getMonthImportTotal(month),
            ])
            .flat(),
          this.totalExportTeus,
          this.totalImportTeus,
          this.totalTeus,
        ];
        excelData.push(totalRow);

        // Create worksheet
        const ws = XLSX.utils.aoa_to_sheet(excelData);

        // Set column widths
        ws["!cols"] = [
          { wch: 6 }, // S.No
          { wch: 40 }, // Client Name
          { wch: 15 }, // Created At
          { wch: 10 }, // Type
          { wch: 15 }, // Branch Name
          { wch: 20 }, // Nomination Shipper
          { wch: 25 }, // Created By
          ...this.uniqueMonths
            .map(() => [
              { wch: 10 }, // Total
              { wch: 10 }, // Export
              { wch: 10 }, // Import
            ])
            .flat(),
          { wch: 15 }, // Export Total
          { wch: 15 }, // Import Total
          { wch: 12 }, // Total
        ];

        // Create workbook
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Client TEUs Data");

        // Generate filename with current date
        const date = new Date();
        const clientPrefix = this.isFromSalesPersonDashboard
          ? this.selectedClientName.replace(/[^a-zA-Z0-9]/g, "_") + "_"
          : "";
        const filename =
          clientPrefix +
          "TEUs_" +
          date.getFullYear() +
          ("0" + (date.getMonth() + 1)).slice(-2) +
          ("0" + date.getDate()).slice(-2) +
          ".xlsx";

        // Write file
        XLSX.writeFile(wb, filename);
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.errorMessage = "Failed to export to Excel. Please try again.";
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    getDateRangeString() {
      if (
        this.Filter.dateRange &&
        this.Filter.dateRange.startDate &&
        this.Filter.dateRange.endDate
      ) {
        const startYear = new Date(
          this.Filter.dateRange.startDate,
        ).getFullYear();
        const endYear = new Date(this.Filter.dateRange.endDate).getFullYear();
        return `${startYear}-${endYear}`;
      }
      return undefined;
    },
    getSalesPersonName(userId) {
      const userList = this.$store.getters["common/getAllUserList"] || [];
      const user = userList.find((u) => u.userId == userId);
      return user ? user.employeeName : "Unknown Sales Person";
    },
    navigateToSalesPersonDashboard(client) {
      // Navigate to sales person dashboard with client filtering
      const clientData = {
        clientId: this.isFromSalesPersonDashboard
          ? this.selectedClientId
          : this.getClientIdFromName(
              client.clientName || client.salesPersonName,
            ),
        clientName: client.clientName || client.salesPersonName,
        salesPersonId: this.Filter.salesPersonFilter,
      };

      this.$router.push({
        path: "/dashboard/sales-person-dashboard",
        query: {
          clientFilter: clientData.clientId,
          clientName: clientData.clientName,
          salesPersonFilter: clientData.salesPersonId,
          fromTeusDashboard: "true",
        },
      });
    },
    getClientIdFromName(clientName) {
      // Find client ID from client name in the processed data
      const clientData = this.processedTableData.find(
        (c) => (c.clientName || c.salesPersonName) === clientName,
      );
      return clientData ? this.getClientIdFromData(clientData) : null;
    },
    getClientIdFromData(clientData) {
      // This is a helper method to extract client ID
      // In the API response, we might need to look it up from the store or from the data structure
      const clientList = this.$store.getters["common/getAllClientList"] || [];
      const client = clientList.find(
        (c) =>
          c.clientName ===
          (clientData.clientName || clientData.salesPersonName),
      );
      return client ? client.clientId : null;
    },
  },
};
</script>

<style scoped>
.client-teus-dashboard {
  padding: 20px;
  background-color: #f4f6f9;
  min-height: 100vh;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dashboard-title {
  margin: 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

.client-info {
  margin: 8px 0 0 0;
  color: #666;
  font-size: 16px;
}

.client-info i {
  margin-right: 8px;
  color: #007bff;
}

.selected-info {
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

/* Client name cell styling for clickable rows */
.client-name-cell {
  transition: all 0.2s ease;
}

.client-name-cell:hover {
  background-color: #f8f9fa;
  border-radius: 4px;
}

.client-name-cell strong {
  color: #007bff;
}

.client-name-cell:hover strong {
  color: #0056b3;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.header-actions .btn {
  padding: 8px 16px;
  font-size: 14px;
}

.loading-container {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.dashboard-content {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Summary Cards */
.summary-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 15px;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.card-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.card-icon.bg-primary {
  background-color: #007bff;
}
.card-icon.bg-success {
  background-color: #28a745;
}
.card-icon.bg-info {
  background-color: #17a2b8;
}
.card-icon.bg-warning {
  background-color: #ffc107;
}

.card-content h4 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.card-content p {
  margin: 5px 0 0 0;
  font-size: 14px;
  color: #666;
}

/* Chart Cards */
.chart-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.chart-title {
  margin: 0 0 20px 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #007bff;
  padding-bottom: 10px;
  display: flex;
  align-items: center;
}

.chart-title i {
  margin-right: 8px;
  color: #007bff;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid #007bff;
}

.chart-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-label {
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin: 0;
}

/* Table Card */
.table-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-title {
  margin: 0 0 20px 0;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  border-bottom: 3px solid #007bff;
  padding-bottom: 15px;
  display: flex;
  align-items: center;
}

.table-title i {
  margin-right: 10px;
  color: #007bff;
}

.table {
  margin-bottom: 0;
}

/* Excel-like Table Styling */
.excel-header {
  background-color: #4472c4 !important;
  color: white !important;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #2f4f7f;
  text-align: center;
  padding: 8px 6px;
  position: sticky;
  top: 0;
  z-index: 10;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.excel-month-header {
  background-color: #4472c4 !important;
  color: white !important;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #2f4f7f;
  text-align: center;
  padding: 8px 6px;
  position: sticky;
  top: 0;
  z-index: 10;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.excel-total-header {
  background-color: #4472c4 !important;
  color: white !important;
  font-weight: 700;
  font-size: 12px;
  border: 1px solid #2f4f7f;
  text-align: center;
  padding: 8px 6px;
  position: sticky;
  top: 0;
  z-index: 10;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.excel-sub-header {
  font-weight: 600;
  font-size: 11px;
  border: 1px solid #2f4f7f;
  text-align: center;
  padding: 6px 4px;
  position: sticky;
  top: 40px;
  z-index: 9;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
}

.excel-sub-header.excel-total {
  background-color: #d9e2f3 !important;
  color: #2f4f7f !important;
}

.excel-sub-header.excel-export {
  background-color: #e2efda !important;
  color: #2f4f7f !important;
}

.excel-sub-header.excel-import {
  background-color: #fff2cc !important;
  color: #2f4f7f !important;
}

.excel-cell {
  border: 1px solid #d0d7de;
  text-align: center;
  padding: 6px 4px;
  font-size: 11px;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  vertical-align: middle;
}

.excel-total-cell {
  background-color: #f8f9fa;
}

.excel-export-cell {
  background-color: #f0f8ff;
}

.excel-import-cell {
  background-color: #fffef0;
}

.excel-footer-row {
  background-color: #e7e6e6 !important;
}

.excel-footer-label {
  background-color: #e7e6e6 !important;
  font-weight: 700;
  border: 1px solid #2f4f7f;
  text-align: center;
  padding: 8px 6px;
}

.table thead th:first-child {
  text-align: left;
  padding-left: 15px;
}

.table thead th:nth-child(2) {
  text-align: left;
  padding-left: 15px;
}

.table tbody tr:hover {
  background-color: #f8f9fa;
}

.table tbody td {
  cursor: help;
  transition: all 0.2s ease;
}

.table tbody td:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.color-legend {
  text-align: right;
}

@media (max-width: 768px) {
  .color-legend {
    text-align: left;
    margin-top: 10px;
  }

  .color-legend small {
    display: block;
    margin-bottom: 5px;
  }
}

.no-data-container {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.no-data-container h4 {
  color: #333;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .header-actions .btn {
    width: 100%;
  }

  .summary-card {
    margin-bottom: 15px;
  }

  .chart-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .chart-controls {
    width: 100%;
    justify-content: space-between;
  }

  .chart-title {
    font-size: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// SalesPersonTeusDashboard.vue?17a527a7